import { IUser, IUserRole } from '@monetize/types/app';
import { useEffect, useRef, useState } from 'react';
import { MBox, MFlex, MText, MTooltip } from '../../../components/Monetize';
import {
  MCustomMultiSelect,
  MCustomMultiSelectRef,
} from '../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import {
  handleTransformRoleLabel,
  renderRoleItemContent,
} from '../../../components/Users/InviteForm';
import { pluralize } from '../../../utils';

interface UserRoleDropdownProps {
  user: IUser;
  userRoles: IUserRole[];
  onChangeRole: (user: IUser, ev: IUserRole[]) => void;
  isLoading?: boolean;
}

/**
 * Multi-select dropdown for user roles
 *
 * This component ensures the multi-select dropdown is always in sync with the user roles
 * There was an issue where the dropdown would not always retain the updated state of the user roles
 *
 */
export const UserRoleDropdown = ({
  user,
  userRoles,
  onChangeRole,
  isLoading = false,
}: UserRoleDropdownProps) => {
  const inputRef = useRef<MCustomMultiSelectRef>(undefined);
  const [processedRoles, setProcessedRoles] = useState(() =>
    user.roles.map((role) => {
      return {
        roleName: handleTransformRoleLabel(role.name),
        ...role,
      };
    }),
  );

  useEffect(() => {
    setProcessedRoles(
      user.roles.map((role) => {
        return {
          roleName: handleTransformRoleLabel(role.name),
          ...role,
        };
      }),
    );
  }, [user.roles]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.updateItems(processedRoles);
    }
  }, [processedRoles]);

  return (
    <MFlex>
      <MCustomMultiSelect
        mt={0}
        ref={inputRef}
        name="roleIds"
        items={userRoles}
        itemTitle="roleName"
        itemValue="id"
        returnItem
        width="full"
        inputWidth="75%"
        value={processedRoles}
        renderItemContent={renderRoleItemContent}
        onChange={(ev) => onChangeRole(user, ev)}
        closeButtonText="Apply"
        isCloseButtonLoading={isLoading}
        isLazy
        usePortal
        renderContentInInput={({ onToggleOpen }) => {
          return (
            <MFlex
              alignItems="center"
              gap={2}
              onMouseDown={(e) => {
                e.stopPropagation();
                onToggleOpen?.();
              }}
            >
              {processedRoles.length === 1 ? (
                <MText fontSize="sm" w={'100%'}>
                  {processedRoles[0].roleName}
                </MText>
              ) : (
                <MTooltip
                  label={
                    <MBox>
                      <MText color="tWhite.base" fontSize={12}>
                        Roles:
                      </MText>
                      {processedRoles.map((role) => (
                        <MText key={role.id} color="tWhite.base" fontSize={12}>
                          {role.roleName}
                        </MText>
                      ))}
                    </MBox>
                  }
                  placement="bottom-start"
                >
                  <MText fontSize="sm">
                    {processedRoles.length}{' '}
                    {pluralize('Role', processedRoles.length)}
                  </MText>
                </MTooltip>
              )}
            </MFlex>
          );
        }}
      />
    </MFlex>
  );
};
